/**
 * Timetable result shaping shared by the client search UI and the server
 * renderers (SSR route pages, Markdown representations).
 *
 * Pure — safe to import from both a client component and a route handler.
 */
import type { BusTimetable } from "../page";

/**
 * Identity of a physical departure, independent of which selected station
 * matched it.
 *
 * Upstream returns one row per (trip, matching station) pair, so selecting
 * several destinations — including the near-duplicate station records the
 * upstream data carries for one place, e.g. BAWANIA / BAWANIYA, or BHOJAWAS /
 * BHOJAWAS (KANINA) / BHOJAWASH — repeats the same 8:00 AM bus once per match.
 *
 * `tripTime` is deliberately part of the identity: the same duty and trip also
 * appears once per selected *boarding* station, at the different time it calls
 * at each one. Those are genuinely different departures for the passenger and
 * must survive de-duplication.
 */
export function busIdentity(bus: BusTimetable): string {
  const { dutyID, tripID } = bus;
  // Typed as numbers, but cached JSON rows predating these fields can arrive
  // without them — fall back to what a passenger would use to tell two
  // departures apart.
  if (!Number.isFinite(dutyID) && !Number.isFinite(tripID)) {
    return [bus.tripTime, bus.tripName, bus.vehicleNumber, bus.depotName].join("|");
  }
  return `${dutyID}|${tripID}|${bus.tripTime}`;
}

/**
 * Identity of a *scheduled* departure, ignoring which duty record covers it.
 *
 * Upstream can carry more than one duty for a single scheduled trip — the same
 * `tripID` at the same time under different depots, where one record has no
 * vehicle allocated at all. A passenger is looking at one bus, so those rows
 * must collapse too. Returns null when there is no trip id to key on, in which
 * case only the exact-duty identity applies.
 */
export function scheduledIdentity(bus: BusTimetable): string | null {
  if (!Number.isFinite(bus.tripID)) return null;
  return `${bus.tripID}|${bus.tripTime}|${bus.tripName ?? ""}`;
}

/** How much a passenger can learn from a row — used to pick between duplicates. */
function informationScore(bus: BusTimetable): number {
  let score = 0;
  if (bus.vehicleType1) score += 2;
  if (bus.vehicleNumber) score += 2;
  if (bus.depotName) score += 1;
  return score;
}

/**
 * Collapse repeated rows for the same departure.
 *
 * Two passes, because upstream repeats a departure for two different reasons:
 * once per matching station (the destination/boarding fan-out), and once per
 * duty record covering the same scheduled trip. Order follows first appearance,
 * so a caller's chronological sort survives; where duplicates disagree, the row
 * carrying the most detail wins, since the empty one is an unallocated duty.
 */
export function dedupeBuses(buses: BusTimetable[]): BusTimetable[] {
  const chosen = new Map<string, BusTimetable>();
  const order: string[] = [];

  for (const bus of buses) {
    const key = scheduledIdentity(bus) ?? busIdentity(bus);
    const existing = chosen.get(key);
    if (!existing) {
      chosen.set(key, bus);
      order.push(key);
      continue;
    }
    if (informationScore(bus) > informationScore(existing)) chosen.set(key, bus);
  }

  return order.map((key) => chosen.get(key) as BusTimetable);
}
